import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner } from '../ui.jsx';

const STATUS = ['pending','filed','approved','rejected','expired','cancelled'];
const STATUS_COLOR = { pending:G.yellow, filed:G.cyan, approved:G.green, rejected:G.red, expired:G.textMuted, cancelled:G.textMuted };

export function Permits({ toast }) {
  const [permits, setPermits] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => Promise.all([
    api('/api/permits'), api('/api/contacts').catch(()=>[]), api('/api/jobs').catch(()=>[]),
  ]).then(([p,c,j]) => { setPermits(p); setContacts(c); setJobs(j); }).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  const followUp = async p => {
    try { const r = await api(`/api/permits/${p.id}/followup`, { method:'POST', body:{}}); load(); toast(`Follow-up sent (${[r.sent?.sms&&'SMS', r.sent?.email&&'email'].filter(Boolean).join(', ')||'logged'})`); }
    catch(e) { toast(e.message,'error'); }
  };
  const remove = async p => {
    if (!confirm('Delete this permit?')) return;
    try { await api(`/api/permits/${p.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(e) { toast(e.message,'error'); }
  };

  if (loading) return <Spinner sz={32}/>;
  const dueSoon = permits.filter(p => p.daysToFollowup !== null && p.daysToFollowup <= 0 && !['approved','expired','cancelled'].includes(p.status));

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Permits <span style={{ color:G.textMuted, fontWeight:500, fontSize:18 }}>({permits.length})</span></h1>
      <Btn v="primary" onClick={()=>setEditing({ status:'pending' })}>+ New Permit</Btn>
    </div>

    {dueSoon.length > 0 && <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor:G.yellow+'66', background:G.yellowLight }}>
      <strong style={{ color:G.yellow }}>⏰ {dueSoon.length} permit{dueSoon.length>1?'s':''} due for follow-up</strong>
      <div style={{ fontSize:13, color:G.textMuted, marginTop:6 }}>The Permit SMS Agent runs automatically. Click "Follow Up" on any row for a manual nudge.</div>
    </div>}

    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['#','Type','Authority','Status','Filed','Next Follow-up','Actions'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
        <tbody>{permits.map(p => (
          <tr key={p.id} onClick={()=>setEditing(p)} style={{ cursor:'pointer' }}>
            <td style={{ ...styles.td, fontFamily:'monospace', fontSize:12 }}>{p.permitNumber||'—'}</td>
            <td style={styles.td}>{p.type}</td>
            <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{p.issuingAuthority||'—'}</td>
            <td style={styles.td}><Badge c={STATUS_COLOR[p.status]} bg={STATUS_COLOR[p.status]+'22'}>{p.status}</Badge></td>
            <td style={{ ...styles.td, fontSize:12 }}>{p.filedDate||'—'}</td>
            <td style={{ ...styles.td, fontSize:12 }}>
              {p.daysToFollowup !== null ? <span style={{ color: p.daysToFollowup<=0?G.red:p.daysToFollowup<3?G.yellow:G.text }}>
                {p.daysToFollowup<=0?`Now (${p.followupCount} sent)`:`In ${p.daysToFollowup}d`}
              </span> : '—'}
            </td>
            <td style={styles.td} onClick={e=>e.stopPropagation()}>
              <div style={{ display:'flex', gap:6 }}>
                <Btn sz="xs" v="cyan" onClick={()=>followUp(p)}>📱 Follow Up</Btn>
                <Btn sz="xs" v="danger" onClick={()=>remove(p)}>×</Btn>
              </div>
            </td>
          </tr>
        ))}</tbody>
      </table>
      {permits.length === 0 && <div style={{ padding:48, textAlign:'center', color:G.textMuted }}>No permits yet. Track every permit you file — the SMS agent auto-follows up every 5 days until approved.</div>}
    </div>

    <PermitModal permit={editing} contacts={contacts} jobs={jobs} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }}/>
  </div>;
}

function PermitModal({ permit, contacts, jobs, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(permit||{}); }, [permit]);
  if (!permit) return null;
  const isNew = !f.id;
  const save = async () => {
    if (!f.type) return alert('Type required');
    try {
      if (isNew) await api('/api/permits', { method:'POST', body: f });
      else await api(`/api/permits/${f.id}`, { method:'PATCH', body: f });
      onSaved();
    } catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={680}>
    <h2 style={{ margin:'0 0 14px 0' }}>{isNew?'New Permit':`${f.permitNumber||f.type}`}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Type *"><Input value={f.type||''} onChange={e=>setF({...f,type:e.target.value})} placeholder="HVAC change-out, Electrical panel..."/></Field>
      <Field label="Permit #"><Input value={f.permitNumber||''} onChange={e=>setF({...f,permitNumber:e.target.value})}/></Field>
      <Field label="Issuing Authority" style={{ gridColumn:'1/-1' }}><Input value={f.issuingAuthority||''} onChange={e=>setF({...f,issuingAuthority:e.target.value})} placeholder="Tampa Building Dept., Hillsborough County..."/></Field>
      <Field label="Job">
        <Select value={f.jobId||''} onChange={e=>setF({...f,jobId:e.target.value})}>
          <option value="">— none —</option>
          {jobs.map(j => <option key={j.id} value={j.id}>{j.name} ({j.contact||''})</option>)}
        </Select>
      </Field>
      <Field label="Customer">
        <Select value={f.contactId||''} onChange={e=>setF({...f,contactId:e.target.value})}>
          <option value="">— none —</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
        </Select>
      </Field>
      <Field label="Status">
        <Select value={f.status||'pending'} onChange={e=>setF({...f,status:e.target.value})}>
          {STATUS.map(s => <option key={s}>{s}</option>)}
        </Select>
      </Field>
      <Field label="Filed Date"><Input type="date" value={f.filedDate||''} onChange={e=>setF({...f,filedDate:e.target.value})}/></Field>
      <Field label="Approved Date"><Input type="date" value={f.approvedDate||''} onChange={e=>setF({...f,approvedDate:e.target.value})}/></Field>
      <Field label="Expiration"><Input type="date" value={f.expirationDate||''} onChange={e=>setF({...f,expirationDate:e.target.value})}/></Field>
      <Field label="Inspection Date"><Input type="date" value={f.inspectionDate||''} onChange={e=>setF({...f,inspectionDate:e.target.value})}/></Field>
      <Field label="Fee"><Input type="number" step="0.01" value={f.fee||0} onChange={e=>setF({...f,fee:Number(e.target.value)})}/></Field>
      <Field label="Authority Phone"><Input value={f.contactPhone||''} onChange={e=>setF({...f,contactPhone:e.target.value})} placeholder="(813) 555-1234"/></Field>
      <Field label="Authority Email"><Input value={f.contactEmail||''} onChange={e=>setF({...f,contactEmail:e.target.value})}/></Field>
    </div>
    <Field label="Notes" style={{ marginTop:8 }}><Input value={f.notes||''} onChange={e=>setF({...f,notes:e.target.value})}/></Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}
